import { graphql } from 'gatsby';
import { PDFViewer } from 'components/PDFViewer';
import { AudioPlayer } from '@src/components/Audio';
import * as styles from './Preview.module.css';
import { DatoDocumentType } from '@src/components/Document/PageTemplates/Category/Three/query';
import React from 'react';
import { MathJaxContext } from 'better-react-mathjax';
import { SEO } from '@src/components';
import { isFallbackUrl } from '@src/utils/documents/url';
import TinyMCEViewer from '@src/components/Common/TinyMCEViewer/TinyMCEViewer';
import Breadcrumbs from '@src/components/Common/Breadcrumbs/Breadcrumbs';
import QuickLinks from '@src/components/BlockFactory/QuickLinks';
import { getQuickLinksToRender, hasQuickLinks } from '@src/utils/documents/quicklinks';
import * as api from '@src/utils/mmc-api/api';
import toast from 'react-hot-toast';
import { BookmarkButton } from '@src/components/Bookmark';
import { trackDocumentClick } from '@src/utils/trackDocumentClick';
interface DocumentPreviewProps {
  data: {
    datoCmsDocument: DatoDocumentType;
    datoCmsGlobal?: {
      audioFiles: {
        id: string;
        title: string;
        fileUrl: string;
        genres: { label: string }[];
      }[];
    };
    allDatoCmsGenre?: {
      edges: { node: { label: string } }[];
    };
  };
}

function toTitleCase(str: string) {
  const capitalised = str.replace(
    /\w\S*/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase(),
  );
  const acronymsCapitalised = capitalised.replace(
    /\d\w(\W|$)/g,
    (text) => text.charAt(0).toUpperCase() + text.substring(1).toUpperCase(),
  );
  return acronymsCapitalised;
}

const courseNameToTitleName = (courseName: string) => {
  let str = courseName.replace(' GCE', '');
  return str;
};

const docNameToTitleName = (docName: string) => {
  let str = docName.toLowerCase();
  str = str.replace(' detailed', '');
  str = toTitleCase(str);
  str = str.replace(' Ms', ' MS');
  return str;
};

export const generateMetaTitle = (doc: DatoDocumentType) => {
  const examBoard = doc.docExamBoard?.[0]?.name || '';
  const course = doc.docExamCourse?.[0]?.name || '';

  const titleParts = [];
  if (examBoard) titleParts.push(examBoard);
  if (course) titleParts.push(courseNameToTitleName(course));
  titleParts.push('Maths');
  titleParts.push(docNameToTitleName(doc.name));

  return titleParts.join(' ');
};

const docNameToDescriptionName = (docName: string) => {
  let str = docName.toLowerCase();
  str = str.replace(' detailed written ms', ' detailed written model solutions');
  str = str.replace(' written ms', ' detailed written model solutions');
  str = str.replace(' ms', ' mark scheme');
  str = str.replace(' mechs ', ' mechanics ');
  str = str.replace(' stats ', ' statistics ');
  return toTitleCase(str);
};

export const generateMetaDescription = (doc: DatoDocumentType) => {
  // const docType = removeTrailingS(doc.docType?.name?.toLowerCase()) || 'document';
  // let docSubType = undefined;
  // switch (doc.docPastPaperType?.name) {
  //   case 'Written Mark Scheme':
  //     docSubType = 'detailed written model solutions'
  //     break;
  //   case 'Mark Scheme':
  //     docSubType = 'mark scheme'
  //     break;
  //   default:
  //     docSubType = undefined;
  // }
  const examBoard = doc.docExamBoard?.[0]?.name || '';
  const course = doc.docExamCourse?.[0]?.name || '';
  const code = doc.docExamCourseCode?.[0]?.name || '';
  // const modules = doc.docExamModules?.map((m) => m.name).join(', ') || '';
  const tier = doc.docExamTier?.[0]?.name || '';
  const examYear = doc.docExamYear;

  const parts = [];
  parts.push('Here you will find the');
  if (examYear) parts.push(examYear);
  if (examBoard) parts.push(examBoard);
  if (course) parts.push(course);
  parts.push(docNameToDescriptionName(doc.name));

  if (doc.docType?.name === 'Textbooks') {
    parts.push('textbook');
  }

  // parts.push(docType);
  // if (docSubType) parts.push(docSubType);
  parts.push('for the');
  if (examBoard) parts.push(examBoard);
  if (course) parts.push(course);
  if (code) parts.push(code);
  // if (modules) parts.push(modules);
  if (tier) parts.push(tier);
  parts.push('Maths course at MyMathsCloud.');
  return parts.join(' ');
};

function buildBreadcrumbs(document?: DatoDocumentType): {
  module?: { url: string; label: string };
  categories?: Array<{ url: string; label: string }>;
  showBreadcrumb: boolean;
} {
  if (!document?.module) {
    return { showBreadcrumb: false };
  }

  const module = {
    label: document.module.title,
    url: `/modules/${document.module.slug}`,
  };

  const categories = document.documentCategory ?? [];

  const mappedCategories = categories.map((category, index) => ({
    label: category.name,
    url:
      `${module.url}/` +
      categories
        .slice(0, index + 1)
        .map((c) => c.slug)
        .join('/'),
  }));

  return {
    module,
    categories: mappedCategories,
    showBreadcrumb: mappedCategories.length > 0,
  };
}

const DocumentPreview = ({ data }: DocumentPreviewProps) => {
  const { datoCmsDocument: document } = data;
  console.log({ document });

  const fileUrl = document.fileUrl.includes('https://mymathscloud.ams3.digitaloceanspaces.com/')
    ? unescape(document.fileUrl.replace('https://mymathscloud.ams3.digitaloceanspaces.com/', ''))
    : document.fileUrl;

  const downloadLink = `${process.env.GATSBY_BACKEND_URL}/api/download/modules/${fileUrl}?id=${document.originalId}`;

  const examBoard = document.docExamBoard?.length
    ? document.docExamBoard?.map((m) => m.name).join(', ')
    : undefined;
  const course = document.docExamCourse?.length
    ? document.docExamCourse?.map((m) => m.name).join(', ')
    : undefined;
  const modules = document.docExamModules?.length
    ? document.docExamModules?.map((m) => m.name).join(', ')
    : undefined;
  const examYear = document.docExamYear;

  const SEOTitle = generateMetaTitle(document);
  const SEODesctiption = generateMetaDescription(document);
  const SEOShouldNoIndex = isFallbackUrl(document);

  const { showBreadcrumb, module, categories } = buildBreadcrumbs(document);

  const quickLinks = getQuickLinksToRender(document, document.module.slug);

  const audioFiles = data.datoCmsGlobal?.audioFiles || [];
  const genres = data.allDatoCmsGenre?.edges?.map((edge) => edge.node) || [];

  return (
    <MathJaxContext>
      <SEO
        description={SEODesctiption}
        title={SEOTitle}
        schema={undefined}
        noIndex={SEOShouldNoIndex}
        seoKeywords={undefined}
      />

      <article className="content-section container">
        {showBreadcrumb && <Breadcrumbs crumbs={[module!, ...categories!]} />}
        <br />
        <div className={styles.metaContent}>
          <h2>{document.name}</h2>
          {examBoard && (
            <span>
              <b>Exam Board:</b> {examBoard}
            </span>
          )}
          {course && (
            <span>
              <b>Course:</b> {course}
            </span>
          )}
          {modules && (
            <span>
              <b>Modules:</b> {modules}
            </span>
          )}
          {examYear && (
            <span>
              <b>Year:</b> {examYear}
            </span>
          )}

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
            <a
              href={downloadLink}
              target="_blank"
              rel="noreferrer"
              className={styles.downloadLink}
              onClick={() =>
                trackDocumentClick(document, {
                  source: 'preview',
                  outcome: 'opened',
                  destinationUrl: downloadLink,
                })
              }
            >
              Download Paper
            </a>

            {document?.originalId && (
              <BookmarkButton style={{ paddingLeft: 0 }} id={document.originalId} showText />
            )}
          </div>

          {/* TODO: Replace
            <button
              style={{ marginLeft: 0 }}
              className="button w-button"
              onClick={handleDocumentSave}
            >
              Save Document
            </button>
          */}
        </div>

        {hasQuickLinks(quickLinks) && (
          <div style={{ marginBottom: 30 }}>
            <QuickLinks block={quickLinks} />
          </div>
        )}

        {audioFiles.length > 0 && (
          <div style={{ marginBottom: 30 }}>
            <AudioPlayer audioFiles={audioFiles} genres={genres} />
          </div>
        )}

        {document.description2 ? (
          <TinyMCEViewer className={styles.description} content={document.description2} />
        ) : null}

        <div className="pb-0.5 flex-1">
          <PDFViewer url={downloadLink} hideWaterMark print />
        </div>
      </article>
    </MathJaxContext>
  );
};

export default DocumentPreview;

const query = graphql`
  query DocumentPreviewQuery($id: String!) {
    datoCmsDocument(id: { eq: $id }) {
      id
      originalId
      hasPassword
      freeSample
      freeDocument
      fileUrl
      externalUrl
      hidePreview
      hideOpenInNewWindow
      description
      name
      displayName
      price
      title
      keywords
      isFeatured
      previewUrl
      fileDescription
      description2
      videoGuide {
        url
        thumbnailUrl
      }
      module {
        slug
        title
        quickLinks {
          title
          links {
            title
            slug
          }
        }
      }
      documentCategory {
        slug
        name
        quickLinks {
          id
          links {
            title
            slug
          }
          module {
            id
            slug
          }
        }
      }
      priceCode
      seo {
        title
        description
        twitterCard
      }
      docType {
        id
        name
      }
      docPastPaperType {
        id
        name
      }
      docExamCourse {
        id
        name
      }
      docExamBoard {
        id
        name
      }
      docExamModules {
        id
        name
      }
      docRelatedTopics {
        id
        name
      }
      docExamCourseCode {
        id
        name
      }
      docExamTier {
        id
        name
      }
      docExamPracticeType {
        id
        name
      }
      docExamTextbookType {
        id
        name
      }
    }
    datoCmsSubscriptionsModal {
      delayForFreePasswordDocuments
    }
    datoCmsGlobal {
      audioFiles {
        id
        title
        fileUrl
        genres {
          label
        }
      }
    }
    allDatoCmsGenre {
      edges {
        node {
          label
        }
      }
    }
  }
`;

export { query };
